import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Packages API
export const packagesAPI = {
  // Get all packages
  getAll: async () => {
    const response = await axios.get(`${API}/packages/`);
    return response.data;
  },

  // Get package by ID
  getById: async (id, includeHiddenDates = false) => {
    const url = `${API}/packages/${id}${includeHiddenDates ? '?include_hidden_dates=true' : ''}`;
    const response = await axios.get(url);
    return response.data;
  },

  // Filter packages
  filter: async (filters) => {
    const response = await axios.post(`${API}/packages/filter`, filters);
    return response.data;
  },

  // Get all destinations
  getDestinations: async () => {
    const response = await axios.get(`${API}/packages/destinations`);
    return response.data;
  },

  // Create new package
  create: async (packageData) => {
    const response = await axios.post(`${API}/packages/`, packageData);
    return response.data;
  },

  // Update package
  update: async (id, packageData) => {
    const response = await axios.put(`${API}/packages/${id}`, packageData);
    return response.data;
  },

  // Delete package
  delete: async (id) => {
    const response = await axios.delete(`${API}/packages/${id}`);
    return response.data;
  }
};

// Reservations API
export const reservationsAPI = {
  // Create reservation
  create: async (reservationData) => {
    const response = await axios.post(`${API}/reservations`, reservationData);
    return response.data;
  },

  // Get reservation by ID
  getById: async (id) => {
    const response = await axios.get(`${API}/reservations/${id}`);
    return response.data;
  },

  // Get customer reservations
  getByEmail: async (email) => {
    const response = await axios.get(`${API}/reservations/customer/${email}`);
    return response.data;
  },

  // Cancel reservation
  cancel: async (id) => {
    const response = await axios.delete(`${API}/reservations/${id}`);
    return response.data;
  }
};

// Payments API
export const paymentsAPI = {
  // Create payment
  create: async (paymentData) => {
    const response = await axios.post(`${API}/payments`, paymentData);
    return response.data;
  },

  // Get payment status
  getStatus: async (paymentId) => {
    const response = await axios.get(`${API}/payments/${paymentId}`);
    return response.data;
  }
};

// Images API
export const imagesAPI = {
  // Get all images
  getAll: async (category = null) => {
    const url = category ? `${API}/images/?category=${category}` : `${API}/images/`;
    const response = await axios.get(url);
    return response.data;
  },

  // Get categories
  getCategories: async () => {
    const response = await axios.get(`${API}/images/categories`);
    return response.data;
  },

  // Upload image
  upload: async (file, altText = '', category = 'geral') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt_text', altText);
    formData.append('category', category);
    const response = await axios.post(`${API}/images/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Delete image
  delete: async (imageId) => {
    const response = await axios.delete(`${API}/images/${imageId}`);
    return response.data;
  }
};

// Pages API - for editable page content (Home, About, Contact)
export const pagesAPI = {
  // Get page content by name
  getByName: async (pageName) => {
    const response = await axios.get(`${API}/pages/${pageName}`);
    return response.data;
  },

  // Save page content
  save: async (pageName, content) => {
    const response = await axios.post(`${API}/pages/${pageName}`, { content });
    return response.data;
  },

  // Get all pages
  getAll: async () => {
    const response = await axios.get(`${API}/pages/`);
    return response.data;
  }
};

// FAQs API
export const faqsAPI = {
  // Get all FAQs
  getAll: async (featured = null) => {
    const url = featured !== null ? `${API}/faqs/?featured=${featured}` : `${API}/faqs/`;
    const response = await axios.get(url);
    return response.data;
  },

  // Get FAQ by ID
  getById: async (id) => {
    const response = await axios.get(`${API}/faqs/${id}`);
    return response.data;
  },

  // Create FAQ
  create: async (faqData) => {
    const response = await axios.post(`${API}/faqs/`, faqData);
    return response.data;
  },

  // Update FAQ
  update: async (id, faqData) => {
    const response = await axios.put(`${API}/faqs/${id}`, faqData);
    return response.data;
  },

  // Delete FAQ
  delete: async (id) => {
    const response = await axios.delete(`${API}/faqs/${id}`);
    return response.data;
  },

  // Bulk update FAQs
  bulkUpdate: async (faqs) => {
    const response = await axios.post(`${API}/faqs/bulk`, faqs);
    return response.data;
  }
};

// Contracts API
export const contractsAPI = {
  // Get all contract templates
  getTemplates: async () => {
    const response = await axios.get(`${API}/contracts/templates`);
    return response.data;
  },

  // Get template by category
  getTemplateByCategory: async (category) => {
    const response = await axios.get(`${API}/contracts/templates/${category}`);
    return response.data;
  },

  // Update template
  updateTemplate: async (templateId, data) => {
    const response = await axios.put(`${API}/contracts/templates/${templateId}`, data);
    return response.data;
  },

  // Generate contract for reservation
  generate: async (data) => {
    const params = new URLSearchParams({
      reservation_id: data.reservation_id,
      customer_name: data.customer_name,
      customer_cpf: data.customer_cpf,
      customer_birth_date: data.customer_birth_date,
      package_name: data.package_name,
      package_category: data.package_category,
      package_id: data.package_id || '',
      travel_date: data.travel_date,
      total_value: data.total_value
    });
    const response = await axios.post(`${API}/contracts/generate?${params.toString()}`);
    return response.data;
  },

  // Accept contract
  accept: async (contractId) => {
    const response = await axios.post(`${API}/contracts/accept/${contractId}`);
    return response.data;
  },

  // Get contract by reservation
  getByReservation: async (reservationId) => {
    const response = await axios.get(`${API}/contracts/reservation/${reservationId}`);
    return response.data;
  },

  // Get all contracts
  getAll: async () => {
    const response = await axios.get(`${API}/contracts/`);
    return response.data;
  }
};

export default {
  packages: packagesAPI,
  reservations: reservationsAPI,
  payments: paymentsAPI,
  images: imagesAPI,
  pages: pagesAPI,
  faqs: faqsAPI,
  contracts: contractsAPI
};
